import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { CheckCircle2, ChevronDown, ChevronUp, History, Loader2 } from "lucide-react";
import KpiCaptureCard from "@/components/ops/KpiCaptureCard";

const FREQ_LABEL = { diaria: "Diarios", semanal: "Semanales", mensual: "Mensuales" };
const FREQ_ORDER = ["diaria", "semanal", "mensual"];

// ── Sección colapsable por frecuencia ────────────────────────
function FreqSection({ label, kpis, onSaved, defaultOpen, userRole, readOnly }) {
  const [open, setOpen] = useState(defaultOpen);
  if (kpis.length === 0) return null;
  return (
    <div className="space-y-3">
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 w-full text-left"
        data-testid={`kpi-section-${label.toLowerCase()}`}
      >
        <span className="text-sm font-bold text-slate-700 uppercase tracking-wide">{label}</span>
        <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{kpis.length}</span>
        <span className="ml-auto text-slate-400">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div className="space-y-3">
          {kpis.map((kpi) => (
            <KpiCaptureCard
              key={`${kpi.kpi_definition_id}-${kpi.branch_id || "corp"}`}
              kpi={kpi}
              onSaved={onSaved}
              userRole={userRole}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Pantalla principal ───────────────────────────────────────
export default function OpsKpisPendientes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");

  const isCorp = user?.role === "corporate_admin" || user?.role === "hr_admin";

  const load = useCallback(async (branchId) => {
    setLoading(true);
    try {
      const params = branchId ? `?branch_id=${branchId}` : "";
      const res = await api.get(`/ops/kpis/my-pending${params}`);
      setPending(res.data.pending || []);
    } catch {
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isCorp) {
      api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
    }
  }, [isCorp]);

  useEffect(() => { load(selectedBranchId); }, [load, selectedBranchId]);

  const handleSaved = useCallback((kpiId, branchId) => {
    setPending((prev) => prev.filter(
      (p) => !(p.kpi_definition_id === kpiId && (p.branch_id || null) === (branchId || null))
    ));
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const kpi of pending) {
      if (!g[kpi.frequency]) g[kpi.frequency] = [];
      g[kpi.frequency].push(kpi);
    }
    return g;
  }, [pending]);

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">Mis KPIs</h1>
            {!loading && (
              <span
                className={`text-sm font-bold px-2.5 py-0.5 rounded-full ${pending.length > 0 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}
                data-testid="kpi-pending-badge"
              >
                {pending.length}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            {isCorp
              ? "Captura los datos crudos. El sistema calcula el indicador por ti."
              : "Consulta tus KPIs del período. El resultado lo captura únicamente RH."}
          </p>
        </div>
        <button
          onClick={() => navigate("/ops/kpis/historico")}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shrink-0"
          data-testid="kpi-history-btn"
        >
          <History className="w-4 h-4" />
          Ver historico
        </button>
      </div>

      {/* Selector de sucursal para corp_admin */}
      {isCorp && branches.length > 0 && (
        <div className="bg-slate-50 rounded-xl p-4 space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">KPIs de sucursal (supervisión)</p>
          <select
            value={selectedBranchId}
            onChange={(e) => setSelectedBranchId(e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            data-testid="kpi-branch-selector"
          >
            <option value="">Sin filtro de sucursal</option>
            {branches.filter((b) => !b.is_corporate).map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <p className="text-xs text-slate-400">Selecciona una sucursal para ver y cargar sus KPIs operativos.</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 h-32 animate-pulse" />
          ))}
        </div>
      ) : pending.length === 0 ? (
        <div className="text-center py-20" data-testid="kpi-empty-state">
          <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <p className="text-lg font-semibold text-slate-700">Sin KPIs pendientes</p>
          <p className="text-sm text-slate-400 mt-1">Todos los KPIs del periodo actual estan al dia.</p>
          <button
            onClick={() => navigate("/ops/kpis/historico")}
            className="mt-4 px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Ver historico
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {FREQ_ORDER.map((freq, idx) => (
            <FreqSection
              key={freq}
              label={FREQ_LABEL[freq]}
              kpis={grouped[freq] || []}
              onSaved={handleSaved}
              defaultOpen={idx === 0}
              userRole={user?.role}
              readOnly={!isCorp}
            />
          ))}
        </div>
      )}
    </div>
  );
}
